/**
 * CLI PSQL Passthrough
 *
 * Allows superadmins to execute read-only SQL queries against the production database.
 */

import process from "node:process";
import { resolveConfig } from "./auth.js";
import { writeRawOutput } from "./brand.js";
import { fetchOrExit } from "./api-fetch.js";

const MAX_QUERY_SIZE = 10_000; // 10KB - reasonable for interactive use

type PsqlResponse = {
  ok: boolean;
  data?: Record<string, unknown>[];
  rowCount?: number;
  truncated?: boolean;
  durationMs?: number;
  error?: string;
};

function getArgValue(argv: string[], key: string): string | undefined {
  const index = argv.indexOf(key);
  if (index === -1 || index >= argv.length - 1) return undefined;
  return argv[index + 1];
}

function hasFlag(argv: string[], ...flags: string[]): boolean {
  return flags.some((flag) => argv.includes(flag));
}

/**
 * Formats query results as a psql-style table.
 */
function formatTable(data: Record<string, unknown>[]): string {
  if (data.length === 0) return "(0 rows)";

  const columns = Object.keys(data[0]);

  // Calculate column widths
  const widths = columns.map((col) =>
    Math.max(col.length, ...data.map((row) => String(row[col] ?? "").length))
  );

  // Build header
  const header = columns.map((col, i) => col.padEnd(widths[i])).join(" | ");
  const separator = widths.map((w) => "-".repeat(w)).join("-+-");

  // Build rows
  const rows = data.map((row) =>
    columns.map((col, i) => String(row[col] ?? "").padEnd(widths[i])).join(" | ")
  );

  return [header, separator, ...rows, `(${data.length} rows)`].join("\n");
}

export async function runPsql(argv: string[]): Promise<void> {
  const { apiUrl, token } = await resolveConfig(argv);

  const jsonOutput = hasFlag(argv, "--json");

  // Get query: either --query value or remaining args joined
  let query = getArgValue(argv, "--query");
  if (!query) {
    // Filter out flags and their values
    const flagsWithValues = ["--api-url", "--token", "--query", "--env"];
    const queryParts: string[] = [];
    for (let i = 0; i < argv.length; i++) {
      if (flagsWithValues.includes(argv[i])) {
        i++; // Skip flag value
        continue;
      }
      if (argv[i].startsWith("--")) continue;
      queryParts.push(argv[i]);
    }
    query = queryParts.join(" ");
  }

  if (!query) {
    console.error("Error: No query provided.");
    console.error("");
    console.error("Usage: canary psql <query> [--json]");
    console.error('       canary psql --query "SELECT * FROM users LIMIT 10"');
    console.error("");
    console.error("Examples:");
    console.error("  canary psql SELECT id, status FROM jobs LIMIT 5");
    console.error('  canary psql "SELECT * FROM jobs WHERE status = \'running\'" --json');
    process.exit(1);
  }

  if (query.length > MAX_QUERY_SIZE) {
    console.error(`Error: Query too large (${query.length} chars, max ${MAX_QUERY_SIZE})`);
    console.error("For large queries, consider using psql directly with appropriate credentials.");
    process.exit(1);
  }

  try {
    const res = await fetchOrExit(`${apiUrl}/superadmin/psql`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      token,
      body: JSON.stringify({ query }),
    });

    // Handle HTTP errors before parsing JSON
    if (!res.ok) {
      const text = await res.text();

      if (res.status === 404) {
        console.error("Error: Endpoint not found. The psql feature may not be deployed to this environment.");
        process.exit(1);
      }

      // Try to parse error as JSON, fallback to raw text
      try {
        const errorJson = JSON.parse(text) as { error?: string };
        console.error(`Error: ${errorJson.error ?? text}`);
      } catch {
        console.error(`Error (${res.status}): ${text || res.statusText}`);
      }
      process.exit(1);
    }

    const json = (await res.json()) as PsqlResponse;

    if (!json.ok) {
      console.error(`Error: ${json.error}`);
      process.exit(1);
    }

    if (jsonOutput) {
      writeRawOutput(JSON.stringify(json.data, null, 2));
    } else {
      writeRawOutput(formatTable(json.data ?? []));
      if (json.truncated) {
        console.log(`\nNote: Results truncated to ${json.rowCount} rows`);
      }
      console.log(`\nTime: ${json.durationMs}ms`);
    }
  } catch (err) {
    console.error(`Failed to execute query: ${err}`);
    process.exit(1);
  }
}
